import React, { useState } from "react";
import Icon from "components/AppIcon";
import Input from "components/ui/Input";
import Button from "components/ui/Button";

const INVESTOR_TYPES = [
  { value: "pension-fund", label: "Pension Fund" },
  { value: "sovereign-wealth", label: "Sovereign Wealth Fund" },
  { value: "family-office", label: "Family Office" },
  { value: "insurance", label: "Insurance Company" },
  { value: "endowment", label: "Endowment / Foundation" },
  { value: "dfi", label: "Development Finance Institution" },
  { value: "fund-of-funds", label: "Fund of Funds" },
  { value: "corporate", label: "Corporate Investor" },
];

const DEFAULT_INVESTORS = [
  {
    id: "inv-1",
    name: "Meridian Pension Trust",
    type: "pension-fund",
    commitment: "15000000",
    allocation: "30",
    status: "confirmed",
    isAnchor: true,
  },
  {
    id: "inv-2",
    name: "Albion Infrastructure Partners",
    type: "family-office",
    commitment: "10000000",
    allocation: "20",
    status: "soft-circle",
    isAnchor: false,
  },
];

function AllocationBar({ investors, targetSize }) {
  const total = investors?.reduce((sum, inv) => sum + (parseFloat(inv?.commitment) || 0), 0);
  const pct = targetSize > 0 ? Math.min((total / targetSize) * 100, 100) : 0;
  const remaining = Math.max(targetSize - total, 0);

  return (
    <div className="p-4 rounded-lg border" style={{ background: "var(--color-muted)", borderColor: "var(--color-border)" }}>
      <div className="flex items-center justify-between mb-2">
        <span className="font-caption text-sm font-medium text-foreground">Capital Allocation Progress</span>
        <span className="font-data text-sm font-semibold" style={{ color: pct >= 100 ? "var(--color-success)" : "var(--color-primary)" }}>
          {pct?.toFixed(1)}%
        </span>
      </div>
      <div className="w-full h-2.5 rounded-full overflow-hidden" style={{ background: "var(--color-border)" }}>
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{
            width: `${pct}%`,
            background: pct >= 100 ? "var(--color-success)" : "var(--color-primary)",
          }}
        />
      </div>
      <div className="flex items-center justify-between mt-2">
        <span className="font-caption text-xs text-muted-foreground">
          Committed: £{total?.toLocaleString("en-GB")}
        </span>
        <span className="font-caption text-xs text-muted-foreground">
          Remaining: £{remaining?.toLocaleString("en-GB")}
        </span>
      </div>
    </div>
  );
}

export default function StepCapTable({ data, onChange, errors }) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newInvestor, setNewInvestor] = useState({
    name: "", type: "family-office", commitment: "", allocation: "", status: "soft-circle", isAnchor: false,
  });

  const investors = data?.investors || DEFAULT_INVESTORS;
  const targetSize = parseFloat(data?.targetSize) || 50000000;

  const handleAddInvestor = () => {
    if (!newInvestor?.name || !newInvestor?.commitment) return;
    const updated = [
      ...investors,
      { ...newInvestor, id: `inv-${Date.now()}` },
    ];
    onChange({ ...data, investors: updated });
    setNewInvestor({ name: "", type: "family-office", commitment: "", allocation: "", status: "soft-circle", isAnchor: false });
    setShowAddForm(false);
  };

  const handleRemove = (id) => {
    onChange({ ...data, investors: investors?.filter((i) => i?.id !== id) });
  };

  const handleToggleAnchor = (id) => {
    onChange({
      ...data,
      investors: investors?.map((i) => i?.id === id ? { ...i, isAnchor: !i?.isAnchor } : i),
    });
  };

  const statusColors = {
    confirmed: { bg: "rgba(5,150,105,0.1)", color: "var(--color-success)", label: "Confirmed" },
    "soft-circle": { bg: "rgba(217,119,6,0.1)", color: "var(--color-warning)", label: "Soft Circle" },
    indicative: { bg: "rgba(59,76,76,0.1)", color: "var(--color-primary)", label: "Indicative" },
  };

  return (
    <div className="space-y-6 lg:space-y-8">
      <div>
        <h3 className="font-heading text-lg lg:text-xl font-semibold text-foreground mb-1">
          Cap Table &amp; Allocations
        </h3>
        <p className="font-caption text-sm text-muted-foreground">
          Define anchor investors, co-investor slots, and capital commitments. Target fund size: £{targetSize?.toLocaleString("en-GB")}.
        </p>
      </div>
      <AllocationBar investors={investors} targetSize={targetSize} />
      {/* Investors Table */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <span className="font-caption text-sm font-semibold text-foreground">
            Investor Commitments ({investors?.length})
          </span>
          <Button
            variant="outline"
            size="sm"
            iconName="Plus"
            iconPosition="left"
            iconSize={14}
            onClick={() => setShowAddForm(true)}
          >
            Add Investor
          </Button>
        </div>

        {/* Desktop table */}
        <div className="hidden md:block overflow-x-auto rounded-lg border" style={{ borderColor: "var(--color-border)" }}>
          <table className="w-full text-sm font-caption">
            <thead>
              <tr style={{ background: "var(--color-muted)", borderBottom: "1px solid var(--color-border)" }}>
                <th className="text-left px-4 py-3 font-semibold text-foreground">Investor</th>
                <th className="text-left px-4 py-3 font-semibold text-foreground">Type</th>
                <th className="text-right px-4 py-3 font-semibold text-foreground">Commitment (£)</th>
                <th className="text-right px-4 py-3 font-semibold text-foreground">Allocation %</th>
                <th className="text-center px-4 py-3 font-semibold text-foreground">Status</th>
                <th className="text-center px-4 py-3 font-semibold text-foreground">Anchor</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {investors?.map((inv, idx) => {
                const sc = statusColors?.[inv?.status] || statusColors?.indicative;
                const allocationPct = targetSize > 0
                  ? ((parseFloat(inv?.commitment) || 0) / targetSize * 100)?.toFixed(1)
                  : inv?.allocation || "—";
                return (
                  <tr
                    key={inv?.id}
                    style={{
                      borderBottom: idx < investors?.length - 1 ? "1px solid var(--color-border)" : undefined,
                      background: inv?.isAnchor ? "rgba(59,76,76,0.04)" : undefined,
                    }}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {inv?.isAnchor && (
                          <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full" style={{ background: "var(--color-primary)" }} />
                        )}
                        <span className="font-medium text-foreground">{inv?.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {INVESTOR_TYPES?.find((t) => t?.value === inv?.type)?.label || inv?.type}
                    </td>
                    <td className="px-4 py-3 text-right font-data font-medium text-foreground">
                      £{parseFloat(inv?.commitment || 0)?.toLocaleString("en-GB")}
                    </td>
                    <td className="px-4 py-3 text-right font-data text-muted-foreground">
                      {allocationPct}%
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span
                        className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold"
                        style={{ background: sc?.bg, color: sc?.color }}
                      >
                        {sc?.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => handleToggleAnchor(inv?.id)}
                        className="transition-all duration-200"
                        title={inv?.isAnchor ? "Remove anchor status" : "Mark as anchor investor"}
                      >
                        <Icon
                          name={inv?.isAnchor ? "Star" : "StarOff"}
                          size={16}
                          color={inv?.isAnchor ? "var(--color-primary)" : "var(--color-muted-foreground)"}
                        />
                      </button>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => handleRemove(inv?.id)}
                        className="text-muted-foreground hover:text-error transition-colors duration-200"
                      >
                        <Icon name="Trash2" size={15} />
                      </button>
                    </td>
                  </tr>
                );
              })}
              {investors?.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground font-caption text-sm">
                    No investors added yet. Click &ldquo;Add Investor&rdquo; to begin building the cap table.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <div className="md:hidden space-y-3">
          {investors?.map((inv) => {
            const sc = statusColors?.[inv?.status] || statusColors?.indicative;
            return (
              <div key={inv?.id} className="p-4 rounded-lg border" style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-caption font-semibold text-foreground text-sm">{inv?.name}</p>
                    <p className="font-caption text-xs text-muted-foreground mt-0.5">
                      {INVESTOR_TYPES?.find((t) => t?.value === inv?.type)?.label}
                    </p>
                  </div>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold flex-shrink-0"
                    style={{ background: sc?.bg, color: sc?.color }}>
                    {sc?.label}
                  </span>
                </div>
                <div className="flex items-center justify-between mt-3">
                  <span className="font-data text-sm font-semibold text-foreground">
                    £{parseFloat(inv?.commitment || 0)?.toLocaleString("en-GB")}
                  </span>
                  <div className="flex items-center gap-3">
                    <button onClick={() => handleToggleAnchor(inv?.id)}>
                      <Icon name={inv?.isAnchor ? "Star" : "StarOff"} size={16}
                        color={inv?.isAnchor ? "var(--color-primary)" : "var(--color-muted-foreground)"} />
                    </button>
                    <button onClick={() => handleRemove(inv?.id)} className="text-muted-foreground hover:text-error">
                      <Icon name="Trash2" size={15} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {/* Add Investor Form */}
      {showAddForm && (
        <div className="p-4 lg:p-6 rounded-lg border" style={{ borderColor: "var(--color-primary)", background: "rgba(59,76,76,0.03)" }}>
          <h4 className="font-caption font-semibold text-sm text-foreground mb-4">Add New Investor</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Input
              label="Investor Name"
              type="text"
              placeholder="e.g. Meridian Capital"
              value={newInvestor?.name}
              onChange={(e) => setNewInvestor({ ...newInvestor, name: e?.target?.value })}
              required
            />
            <div>
              <label className="block font-caption text-sm font-medium text-foreground mb-1.5">Investor Type</label>
              <select
                value={newInvestor?.type}
                onChange={(e) => setNewInvestor({ ...newInvestor, type: e?.target?.value })}
                className="w-full h-10 px-3 rounded-md border text-sm font-caption bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                style={{ borderColor: "var(--color-border)" }}
              >
                {INVESTOR_TYPES?.map((t) => <option key={t?.value} value={t?.value}>{t?.label}</option>)}
              </select>
            </div>
            <Input
              label="Commitment (£)"
              type="number"
              placeholder="e.g. 5000000"
              value={newInvestor?.commitment}
              onChange={(e) => setNewInvestor({ ...newInvestor, commitment: e?.target?.value })}
              required
            />
            <div>
              <label className="block font-caption text-sm font-medium text-foreground mb-1.5">Status</label>
              <select
                value={newInvestor?.status}
                onChange={(e) => setNewInvestor({ ...newInvestor, status: e?.target?.value })}
                className="w-full h-10 px-3 rounded-md border text-sm font-caption bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                style={{ borderColor: "var(--color-border)" }}
              >
                <option value="confirmed">Confirmed</option>
                <option value="soft-circle">Soft Circle</option>
                <option value="indicative">Indicative</option>
              </select>
            </div>
            <div className="flex items-end pb-0.5">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={newInvestor?.isAnchor}
                  onChange={(e) => setNewInvestor({ ...newInvestor, isAnchor: e?.target?.checked })}
                  className="w-4 h-4 rounded"
                />
                <span className="font-caption text-sm text-foreground">Anchor Investor</span>
              </label>
            </div>
          </div>
          <div className="flex items-center gap-3 mt-4">
            <Button variant="default" size="sm" onClick={handleAddInvestor}>Add to Cap Table</Button>
            <Button variant="ghost" size="sm" onClick={() => setShowAddForm(false)}>Cancel</Button>
          </div>
        </div>
      )}
      {/* GP Commitment */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
        <Input
          label="GP / Sponsor Commitment (£)"
          type="number"
          placeholder="e.g. 2500000"
          value={data?.gpCommitment || ""}
          onChange={(e) => onChange({ ...data, gpCommitment: e?.target?.value })}
          description="General Partner co-investment alongside LP investors."
        />
        <Input
          label="GP Commitment (%)"
          type="number"
          placeholder="e.g. 5"
          value={data?.gpCommitmentPct || ""}
          onChange={(e) => onChange({ ...data, gpCommitmentPct: e?.target?.value })}
          description="Percentage of total fund size committed by GP."
        />
      </div>
    </div>
  );
}